export interface WebcamConfig {
  name: string;
  lat: number;
  lon: number;
  type: string;
  url: string;
}

export class WebcamModal {
  private container: HTMLElement;

  constructor() {
    this.container = document.createElement('div');
    this.container.id = 'webcam-modal-container';
    this.container.style.display = 'none';
    this.container.style.position = 'fixed';
    this.container.style.top = '0';
    this.container.style.left = '0';
    this.container.style.width = '100vw';
    this.container.style.height = '100vh';
    this.container.style.backgroundColor = 'rgba(0, 0, 0, 0.75)';
    this.container.style.backdropFilter = 'blur(10px)';
    (this.container.style as any).WebkitBackdropFilter = 'blur(10px)';
    this.container.style.zIndex = '10000';
    this.container.style.alignItems = 'center';
    this.container.style.justifyContent = 'center';

    this.container.addEventListener('click', (e) => {
      if (e.target === this.container) {
        this.close();
      }
    });

    window.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.container.style.display !== 'none') {
        this.close();
      }
    });

    document.body.appendChild(this.container);
  }

  public close() {
    this.container.style.display = 'none';
    this.container.innerHTML = '';
  }

  public open(cam: WebcamConfig, dateStr: string, event: string, score: number | null) {
    this.container.style.display = 'flex';
    
    const cleanName = cam.name.replace(/ /g, '_');
    const dateParts = dateStr.split('-');
    const cleanDate = dateStr.replace(/-/g, '');
    
    // Golden hour & twilight solar offsets defined in scrape_webcams.py
    const sunriseOffsets = [
      { offset: -20, label: 'T - 20 min' },
      { offset: -10, label: 'T - 10 min' },
      { offset: -5, label: 'T - 5 min' },
      { offset: 0, label: 'Sunrise (0 min)' },
      { offset: 15, label: 'T + 15 min (Golden Hr)' },
      { offset: 30, label: 'T + 30 min (Golden Hr)' },
      { offset: 60, label: 'T + 60 min (Golden Hr)' }
    ];

    const sunsetOffsets = [
      { offset: -60, label: 'T - 60 min (Golden Hr)' },
      { offset: -30, label: 'T - 30 min (Golden Hr)' },
      { offset: -15, label: 'T - 15 min (Golden Hr)' },
      { offset: 0, label: 'Sunset (0 min)' },
      { offset: 5, label: 'T + 5 min' },
      { offset: 10, label: 'T + 10 min' },
      { offset: 20, label: 'T + 20 min' }
    ];

    const otherCaptures = [
      { key: '0800', label: '8:00 AM Capture', timeStr: '08:00 AM' },
      { key: '1000', label: '10:00 AM Capture', timeStr: '10:00 AM' },
      { key: '1200', label: '12:00 PM (Noon) Capture', timeStr: '12:00 PM' },
      { key: '1400', label: '2:00 PM Capture', timeStr: '02:00 PM' },
      { key: '1600', label: '4:00 PM Capture', timeStr: '04:00 PM' },
      { key: 'Latest-Forced', label: 'Latest Live Scrape', timeStr: 'Latest' }
    ];
    
    const scoreBadge = score !== null 
      ? `<span style="display: inline-block; padding: 2px 8px; border-radius: 4px; font-weight: 700; background: #3b82f6; color: #fff; margin-left: 5px;">${score}</span>`
      : `<span style="display: inline-block; padding: 2px 8px; border-radius: 4px; font-weight: 700; background: #334155; color: #94a3b8; margin-left: 5px;">--</span>`;

    const renderCard = (filename: string, titleLabel: string, subLabel?: string, isLatestLive: boolean = false) => {
      const imgPath = `webcams/${dateParts[0]}/${dateParts[1]}/${dateParts[2]}/${filename}`;
      const fallbackAttr = isLatestLive
        ? `onerror="if(!this.dataset.fallback){this.dataset.fallback='1'; this.src='webcams/latest/${cleanName}.jpg';}else{this.style.display='none'; this.nextElementSibling.style.display='flex';}"`
        : `onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';"`;

      return `
        <div style="background: #1e293b; border: 1px solid var(--border-light, #334155); padding: 10px; border-radius: 8px; display: flex; flex-direction: column; gap: 6px; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.2);">
          <div style="display: flex; justify-content: space-between; align-items: baseline;">
            <span style="font-size: 0.75rem; color: #94a3b8; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px;">${titleLabel}</span>
            ${subLabel ? `<span style="font-size: 0.7rem; color: #64748b;">${subLabel}</span>` : ''}
          </div>
          <div style="position: relative; width: 100%; aspect-ratio: 3/2; background: #0a0e17; border-radius: 4px; overflow: hidden;">
            <img src="${imgPath}" ${fallbackAttr} onclick="window.open(this.src)" style="width: 100%; height: 100%; object-fit: cover; cursor: pointer; transition: transform 0.2s;" onmouseenter="this.style.transform='scale(1.02)'" onmouseleave="this.style.transform='scale(1)'">
            <div style="display: none; width: 100%; height: 100%; align-items: center; justify-content: center; color: #475569; font-size: 0.8rem; font-weight: 600; font-family: sans-serif;">
              No Capture Available
            </div>
          </div>
        </div>
      `;
    };

    const html = `
      <div style="width: 92vw; max-width: 1500px; height: 90vh; background: #0f172a; border-radius: 12px; border: 1px solid var(--border-light, #334155); display: flex; flex-direction: column; overflow: hidden; box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.6);">
        
        <!-- Header -->
        <div style="padding: 16px 24px; border-bottom: 1px solid var(--border-light, #334155); display: flex; justify-content: space-between; align-items: center; background: #1e293b; gap: 16px;">
          <div style="display: flex; flex-direction: column; gap: 4px;">
            <div style="display: flex; align-items: center; gap: 10px; flex-wrap: wrap;">
              <h2 style="margin: 0; font-size: 1.3rem; color: #f8fafc; font-weight: 700; display: flex; align-items: center; gap: 8px;">
                <span>📸</span>
                <span>${cam.name.replace(/_/g, ' ')}</span>
              </h2>
              <span style="font-size: 0.72rem; padding: 2px 7px; border-radius: 4px; background: rgba(56, 189, 248, 0.15); color: #38bdf8; font-weight: 600; text-transform: uppercase;">${(cam.type || 'ALL').toUpperCase()}</span>
            </div>
            <div style="color: #94a3b8; font-size: 0.8rem; display: flex; align-items: center; gap: 12px; flex-wrap: wrap;">
              <span>📅 <b>Date:</b> ${dateStr}</span>
              ${event ? `<span>🎯 <b>Layer:</b> ${event.toUpperCase()}</span>` : ''}
              <span>📍 <b>Coords:</b> ${cam.lat.toFixed(4)}°, ${cam.lon.toFixed(4)}°</span>
              <span>🔗 <a href="${cam.url}" target="_blank" rel="noopener noreferrer" style="color: #60a5fa; text-decoration: underline;">Direct Source Link ↗</a></span>
              <span>📈 <b>Forecast Score:</b> ${scoreBadge}</span>
            </div>
          </div>
          <button id="webcam-modal-close" style="background: transparent; border: none; color: #94a3b8; font-size: 2rem; cursor: pointer; line-height: 1; transition: color 0.2s; padding: 4px 8px;" title="Close (Esc)">&times;</button>
        </div>

        <!-- Full-Width Image Grid Content Area -->
        <div style="flex-grow: 1; padding: 24px; overflow-y: auto; display: flex; flex-direction: column; gap: 24px; background: #0b0f19;">
          
          <!-- Sunrise & Morning Golden Hours -->
          <div>
            <h3 style="font-size: 1rem; font-weight: 600; color: #fbbf24; border-bottom: 1px solid #1e293b; padding-bottom: 8px; margin: 0 0 14px 0; display: flex; align-items: center; gap: 8px;">
              🌅 Sunrise & Morning Golden Hours
            </h3>
            <div style="display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 14px;">
              ${sunriseOffsets.map(item => {
                const filename = `${cleanName}-${cleanDate}-Sunrise-${item.offset}m.jpg`;
                return renderCard(filename, item.label);
              }).join('')}
            </div>
          </div>

          <!-- Sunset & Evening Golden Hours -->
          <div>
            <h3 style="font-size: 1rem; font-weight: 600; color: #f97316; border-bottom: 1px solid #1e293b; padding-bottom: 8px; margin: 0 0 14px 0; display: flex; align-items: center; gap: 8px;">
              🌇 Sunset & Evening Golden Hours
            </h3>
            <div style="display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 14px;">
              ${sunsetOffsets.map(item => {
                const filename = `${cleanName}-${cleanDate}-Sunset-${item.offset}m.jpg`;
                return renderCard(filename, item.label);
              }).join('')}
            </div>
          </div>

          <!-- Other Captures (Fixed Daytime & Latest) -->
          <div>
            <h3 style="font-size: 1rem; font-weight: 600; color: #38bdf8; border-bottom: 1px solid #1e293b; padding-bottom: 8px; margin: 0 0 14px 0; display: flex; align-items: center; gap: 8px;">
              🕒 Other Captures (Daytime & Live Verification)
            </h3>
            <div style="display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 14px;">
              ${otherCaptures.map(item => {
                const isLatestLive = item.key === 'Latest-Forced';
                const filename = isLatestLive
                  ? `${cleanName}-${cleanDate}-Latest-Forced.jpg`
                  : `${cleanName}-${cleanDate}-${item.key}.jpg`;
                return renderCard(filename, item.label, `${dateStr} @ ${item.timeStr}`, isLatestLive);
              }).join('')}
            </div>
          </div>

        </div>

      </div>
    `;

    this.container.innerHTML = html;

    // Bind close events
    const closeBtn = document.getElementById('webcam-modal-close');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.close());
    }
  }
}

export const webcamModal = new WebcamModal();
